import { useCallback, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'

import { FormOnChange } from '@procsea/design-system'

import { dispatchCreateNotification } from 'src/action-creators'
import { useAuth } from 'src/contexts/authContext'
import { useUser } from 'src/contexts/userContext'
import { usePasswordUpdate } from 'src/queries/shared/passwordUpdate'
import { User } from 'src/types/user'
import { handleErrorResponse } from 'src/utils/form/handleErrorResponse'
import { requiredRule, shouldValidateAndDirtyConfig } from 'src/utils/form/validationRules.utils'

interface PasswordUpdateFormState {
  newPassword: string
  confirmPassword: string
}

interface PasswordUpdateFormProps {
  onSubmitCallback?: () => void
}

export const usePasswordUpdateForm = ({ onSubmitCallback }: PasswordUpdateFormProps) => {
  const dispatch = useDispatch()
  const user = useUser()
  const { updateUser } = useAuth()

  const { mutate: updatePassword } = usePasswordUpdate()

  const form = useForm<PasswordUpdateFormState>({
    defaultValues: { newPassword: '', confirmPassword: '' },
    mode: 'onBlur',
    reValidateMode: 'onChange',
  })

  const { newPassword } = form.watch()

  const validatePassword = useCallback(
    (value: string) => value === newPassword || gettext('Passwords do not match'),
    [newPassword]
  )

  useEffect(() => {
    form.register('newPassword', {
      ...requiredRule,
      minLength: {
        message: gettext('This password is too short, it must contain at least 8 characters'),
        value: 8,
      },
    })
    form.register('confirmPassword', { ...requiredRule, validate: validatePassword })
  }, [validatePassword])

  const handleInputChange: FormOnChange<string> = useCallback(
    ({ name, value }) =>
      form.setValue(name as keyof PasswordUpdateFormState, value, shouldValidateAndDirtyConfig),
    [form.setValue]
  )

  const handleSubmit = useCallback(
    (data: PasswordUpdateFormState) => {
      const { confirmPassword: confirm, ...formValue } = data

      updatePassword(formValue, {
        onSuccess: () => {
          dispatchCreateNotification(dispatch)({
            type: 'positive',
            message: gettext('Password updated successfully'),
          })
          updateUser({ ...user, userPasswordIsSet: true } as User)
          onSubmitCallback?.()

          form.reset()
        },
        onError: handleErrorResponse({ setError: form.setError, dispatch, formValue }),
      })
    },
    [dispatch, form.setError, form.reset, updatePassword, onSubmitCallback, updateUser, user]
  )

  return { form, handleInputChange, handleSubmit }
}
